import { IChatMessage, ChatState } from '@enuan/chat__utils';
import { RefObject, useCallback, useContext, useEffect, useRef } from 'react';
import { configurationContext } from '../../contexts/configuration';
import { configContext } from 'src/contexts/config';
import { instanceContext } from 'src/contexts/instance';
import { ChannelState } from '../../interfaces/ChannelState';
import { useGetHistory } from './messages.hooks';
import { useLogError } from '../../components/ErrorBoundary/useLogError';
import { HISTORY_PAGE_SIZE } from './types';
import { useMessageSideEffectsHandler } from './messageSideEffects.hooks';
import type { ConnectionInitEffectParams } from './types';

/**
 * Provides a `startConnection` function that tracks remaining retries
 * and transitions to {@link ChannelState.ERROR} when exhausted.
 *
 * @param timestampRef - Ref holding the latest message timestamp forwarded to the provider.
 * @returns `startConnection`, `retriesRef` and `resetRetries`.
 */
export const useStartConnection = (
	timestampRef: RefObject<string | undefined>,
) => {
	const { startConnection: _startConnection } = useContext(instanceContext);
	const { setChannelState } = useContext(configContext);
	const { reconnection_retries = 5 } = useContext(configurationContext);
	const retriesRef = useRef(reconnection_retries);

	const startConnection = useCallback(() => {
		if (retriesRef.current === 0) {
			setChannelState(ChannelState.ERROR);
			throw new Error('Reconnection max retry');
		}
		retriesRef.current -= 1;
		return _startConnection(timestampRef.current);
	}, [_startConnection, setChannelState, timestampRef]);

	const resetRetries = useCallback(() => {
		retriesRef.current = reconnection_retries;
	}, [reconnection_retries]);

	return { startConnection, retriesRef, resetRetries };
};

/**
 * Returns a recursive reconnect function that counts down one second at a time
 * (based on `reconnection_timeout` from configuration) before invoking `startConnection`.
 *
 * @param startConnection - Called once the countdown reaches zero.
 */
export const useReconnect = (startConnection: () => unknown) => {
	const { reconnection_timeout } = useContext(configurationContext);
	const reconnectionTimeoutSeconds = reconnection_timeout
		? reconnection_timeout / 1000
		: 10;

	const reconnect = useCallback(
		(seconds: number = reconnectionTimeoutSeconds) =>
			new Promise((resolve, reject) =>
				window.setTimeout(() => {
					console.log('Reconnection in', seconds);
					if (seconds === 0) {
						try {
							resolve(startConnection());
						} catch (error) {
							reject(error);
						}
					} else {
						resolve(reconnect(seconds - 1));
					}
				}, 1000),
			),
		[reconnectionTimeoutSeconds, startConnection],
	);
	return reconnect;
};

/**
 * Runs once when the provider is available and `state` is ON.
 * Initialises the provider, fetches the first history page, applies message side-effects,
 * stores the oldest timestamp in `timestampRef`, then starts the connection.
 * On history error the side-effects still run with an empty array.
 */
export const useConnectionInitEffect = ({
	timestampRef,
	startConnection,
}: ConnectionInitEffectParams) => {
	const { state } = useContext(configurationContext);
	const { instance: provider } = useContext(instanceContext);
	const getHistory = useGetHistory();
	const logError = useLogError('init');
	const messageHandler = useMessageSideEffectsHandler();

	useEffect(() => {
		console.log('Init Chat Hook', provider, state);
		if (!provider || state?.toLowerCase() !== ChatState.ON.toLowerCase()) {
			return;
		}
		try {
			provider.init();
			getHistory(timestampRef.current, HISTORY_PAGE_SIZE)
				?.catch((error) => {
					logError('getFirstHistory', {
						error,
						payload: { timestamp: timestampRef.current },
					});
					return [] as IChatMessage[];
				})
				.then((messages) => {
					messageHandler(messages);
					const firstMessage = messages?.[0];
					const firstTstamp = firstMessage?._timestamp;
					timestampRef.current = firstTstamp;
					if (!firstTstamp) {
						console.log('No history');
					}
				})
				.finally(() => startConnection());
		} catch (error: unknown) {
			console.error('Init connection error', error);
			logError('initConnection', { error });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- timestampRef is a ref
	}, [provider, state, messageHandler, startConnection, getHistory, logError]);
};
